/**
 * CoachHintDialog — AI-powered Coach Actions dialog.
 *
 * Opens when the author clicks "⚡ Actions" in a CoachHint tooltip.
 * Offers four Claude-powered editing operations for a chapter:
 *   1. Find insertion point   — highlights the best paragraph gap in the editor
 *   2. Adjust pacing          — rewrites with tighter/expanded pacing
 *   3. Adjust character       — revises with focus on character arc & situation
 *   4. Rewrite scene          — full prose rewrite preserving story beats
 *
 * Accepts → replaces chapter content immediately and reloads the project.
 * Highlight → dispatches `loom:highlight-insertion-point` to the editor.
 */
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ArrowUpDown,
  BookOpen,
  CheckCircle2,
  Compass,
  Crosshair,
  ExternalLink,
  FilePlus2,
  Info,
  Loader2,
  MapPin,
  Pencil,
  RefreshCw,
  Sparkles,
  Tag,
  TrendingUp,
  User,
  X,
  Zap,
} from 'lucide-react';
import axios from 'axios';
import { ARCHETYPES } from '@/lib/archetypes';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Compact catalog sent to the backend — id + name + blurb only to keep payload small
const ARCHETYPE_CATALOG = ARCHETYPES.map(({ id, name, blurb }) => ({ id, name, blurb }));

// ── Action definitions ────────────────────────────────────────────────────────

const ACTIONS = [
  {
    id: 'insertion_point',
    icon: MapPin,
    label: 'Find insertion point',
    description: 'Claude identifies the best paragraph gap where new prose should be added, then highlights it in the editor.',
    ctaLabel: 'Highlight in editor',
    color: 'sky',
  },
  {
    id: 'add_scene_beat',
    icon: FilePlus2,
    label: 'Add a scene or beat',
    description: 'Claude generates new prose (140–280 words) for the scene or story beat that best fills the chapter\'s structural gap, then inserts it at the optimal position.',
    ctaLabel: 'Accept & insert',
    color: 'teal',
    functionNote: 'Narrative expansion — generates a new tension_rise / reflection / action / transition beat and rebuilds the full chapter with it inserted.',
  },
  {
    id: 'adjust_pacing',
    icon: ArrowUpDown,
    label: 'Adjust pacing',
    description: 'Claude rewrites the chapter with tighter or expanded pacing — trims slow passages, sharpens transitions.',
    ctaLabel: 'Accept rewrite',
    color: 'amber',
  },
  {
    id: 'adjust_character',
    icon: User,
    label: 'Adjust character situation',
    description: 'Claude revisits the chapter to deepen the main character\'s emotional arc, stakes, and motivation.',
    ctaLabel: 'Accept rewrite',
    color: 'violet',
  },
  {
    id: 'rewrite_scene',
    icon: Pencil,
    label: 'Rewrite scene',
    description: 'Full prose rewrite preserving every plot beat — new sentence structures, stronger verbs, richer detail.',
    ctaLabel: 'Accept rewrite',
    color: 'emerald',
  },
  {
    id: 'suggest_archetype',
    icon: Tag,
    label: 'Suggest character archetype',
    description: 'Claude reads this chapter, identifies the prominent characters, and recommends the best archetype from your catalog — with specific reasoning.',
    ctaLabel: 'View suggestions',
    color: 'orchid',
  },
  {
    id: 'fix_pacing_gap',
    icon: TrendingUp,
    label: 'Fix pacing gap',
    description: 'Claude rewrites this chapter to directly address any pacing issues already flagged by the Full Novel Review — using the continuity score as an editorial brief.',
    ctaLabel: 'Accept rewrite',
    color: 'rose',
  },
];

const COLOR_RING = {
  sky:     'ring-sky-400/60 bg-sky-50 text-sky-800',
  teal:    'ring-teal-400/60 bg-teal-50 text-teal-800',
  amber:   'ring-amber-400/60 bg-amber-50 text-amber-800',
  violet:  'ring-violet-400/60 bg-violet-50 text-violet-800',
  emerald: 'ring-emerald-400/60 bg-emerald-50 text-emerald-800',
  orchid:  'ring-purple-400/60 bg-purple-50 text-purple-800',
  rose:    'ring-rose-400/60 bg-rose-50 text-rose-800',
};

// ── Action button ─────────────────────────────────────────────────────────────

function ActionButton({ action, activeId, loading, onSelect }) {
  const Icon = action.icon;
  const isActive = activeId === action.id;
  return (
    <button
      type="button"
      onClick={() => !loading && onSelect(action.id)}
      disabled={loading && !isActive}
      data-testid={`coach-action-btn-${action.id}`}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition',
        isActive
          ? `ring-2 border-transparent ${COLOR_RING[action.color]}`
          : 'border-panel-border bg-panel-header/40 hover:bg-secondary/60',
        loading && !isActive && 'opacity-40 cursor-not-allowed',
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', isActive ? '' : 'text-primary')} />
      <div className="min-w-0">
        <p className="text-[12px] font-semibold leading-tight">{action.label}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{action.description}</p>
        {/* Function note — shown for actions that generate new content */}
        {action.functionNote && (
          <p className="mt-1 flex items-center gap-1 text-[10.5px] text-muted-foreground/70 italic">
            <Zap className="h-2.5 w-2.5 shrink-0 text-teal-500" />
            {action.functionNote}
          </p>
        )}
      </div>
      {isActive && loading && <Loader2 className="ml-auto mt-1 h-3.5 w-3.5 shrink-0 animate-spin" />}
    </button>
  );
}

// ── Result pane ───────────────────────────────────────────────────────────────

function InsertionPointResult({ result, chapterId, onAccepted }) {
  const handleHighlight = () => {
    window.dispatchEvent(
      new CustomEvent('loom:highlight-insertion-point', {
        detail: {
          chapterId,
          afterParagraphText: result.after_paragraph_text || '',
          paragraphIndex:     result.paragraph_index || 0,
          explanation:        result.explanation || '',
        },
      }),
    );
    toast.success('Insertion point highlighted in editor.');
    onAccepted?.();
  };

  return (
    <div className="space-y-3" data-testid="coach-result-insertion">
      <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3">
        <Crosshair className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
        <div>
          <p className="text-[12px] font-semibold text-sky-900">
            After paragraph {result.insert_after_label || `#${result.paragraph_index}`}
          </p>
          {result.after_paragraph_text && (
            <p className="mt-1 font-mono-stat text-[11px] text-sky-800 italic">
              &ldquo;{result.after_paragraph_text}&rdquo;&hellip;
            </p>
          )}
          <p className="mt-1.5 text-[11.5px] text-sky-900">{result.explanation}</p>
        </div>
      </div>
      {result.suggested_opening && (
        <div className="rounded border border-dashed border-sky-300 bg-sky-50/50 p-2.5">
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-sky-700">Suggested opening line</p>
          <p className="text-[12px] italic text-sky-900">{result.suggested_opening}</p>
        </div>
      )}
      <Button
        size="sm"
        className="w-full bg-sky-600 hover:bg-sky-700 text-white"
        onClick={handleHighlight}
        data-testid="coach-highlight-btn"
      >
        <MapPin className="mr-1.5 h-3.5 w-3.5" />
        Highlight in editor
      </Button>
    </div>
  );
}

// ── Add Scene/Beat result ──────────────────────────────────────────────────────

const BEAT_TYPE_LABELS = {
  tension_rise:         'Tension rise',
  character_reflection: 'Character reflection',
  scene_transition:     'Scene transition',
  action_beat:          'Action beat',
  dialogue_exchange:    'Dialogue exchange',
};

function AddSceneBeatResult({ result, chapterId, onAccepted }) {
  const [accepting, setAccepting] = useState(false);
  const beatLabel = BEAT_TYPE_LABELS[result.beat_type] || result.beat_type || 'New beat';
  const fullChapter = (result.full_chapter || '').trim();
  const newScene   = (result.new_scene   || '').trim();
  const preview    = fullChapter || newScene;

  const handleAccept = async () => {
    if (!preview) return;
    setAccepting(true);
    try {
      await axios.put(
        `${API}/chapters/${chapterId}`,
        { content: fullChapter || newScene },
        { withCredentials: true },
      );
      window.dispatchEvent(
        new CustomEvent('loom:chapter-content-replaced', {
          detail: { chapterId, content: fullChapter || newScene },
        }),
      );
      toast.success(`Scene inserted — ${result.scene_word_count || ''} words added.`);
      onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the new beat.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="coach-result-add-beat">
      {/* Function-used banner — shows exactly what the AI did */}
      <div className="flex items-start gap-2 rounded-lg border border-teal-200 bg-teal-50 p-2.5"
           data-testid="coach-function-used-banner">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-700" />
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-wide text-teal-700 mb-0.5">
            Function used
          </p>
          <p className="text-[12px] text-teal-900 font-medium">
            {result.function_used || `Narrative expansion · ${beatLabel} inserted after ${result.insert_after_label || 'selected paragraph'}`}
          </p>
        </div>
      </div>

      {/* Beat type + insertion badge */}
      <div className="flex flex-wrap gap-2">
        <span className="rounded-full border border-teal-300 bg-teal-50 px-2 py-0.5 text-[10.5px] font-semibold text-teal-800">
          {beatLabel}
        </span>
        {result.insert_after_label && (
          <span className="rounded-full border border-panel-border bg-secondary px-2 py-0.5 text-[10.5px] text-muted-foreground">
            After {result.insert_after_label}
          </span>
        )}
        {result.scene_word_count && (
          <span className="rounded-full border border-panel-border bg-secondary px-2 py-0.5 text-[10.5px] text-muted-foreground">
            +{result.scene_word_count} words
          </span>
        )}
      </div>

      {/* Explanation */}
      {result.explanation && (
        <div className="flex items-start gap-2 rounded border border-panel-border bg-panel-header/50 p-2.5">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-600" />
          <p className="text-[11.5px] text-foreground">{result.explanation}</p>
        </div>
      )}

      {/* New scene preview */}
      {newScene && (
        <div>
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            Generated beat
          </p>
          <ScrollArea className="h-40 rounded border border-teal-200 bg-[var(--manuscript-bg,#fdfaf5)] p-3">
            <p className="whitespace-pre-wrap font-serif text-[12.5px] leading-relaxed text-[#1a1a1a]">
              {newScene}
            </p>
          </ScrollArea>
        </div>
      )}

      {/* Word count delta */}
      {result.original_word_count && (
        <p className="text-[10.5px] text-muted-foreground">
          Chapter: {result.original_word_count} → {result.updated_word_count} words
        </p>
      )}

      <Button
        size="sm"
        className="w-full bg-teal-600 hover:bg-teal-700 text-white"
        onClick={handleAccept}
        disabled={accepting || !preview}
        data-testid="coach-accept-beat-btn"
      >
        {accepting
          ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          : <FilePlus2 className="mr-1.5 h-3.5 w-3.5" />}
        Accept &amp; insert into chapter
      </Button>
    </div>
  );
}

// ── Rewrite result ─────────────────────────────────────────────────────────────

function RewriteResult({ result, chapterId, onAccepted, setChapters }) {  const [accepting, setAccepting] = useState(false);
  const action = ACTIONS.find((a) => a.id === result.action);
  const rewrite = result.rewrite || '';

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.put(
        `${API}/chapters/${chapterId}`,
        { content: rewrite },
        { withCredentials: true },
      );
      // Notify the editor to reload
      window.dispatchEvent(
        new CustomEvent('loom:chapter-content-replaced', { detail: { chapterId, content: rewrite } }),
      );
      toast.success('Chapter updated with Claude\'s rewrite.');
      onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save rewrite.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="coach-result-rewrite">
      {/* Summary */}
      <div className="flex items-start gap-2 rounded-lg border border-panel-border bg-panel-header/50 p-2.5">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
        <p className="text-[11.5px] text-foreground">{result.summary}</p>
      </div>
      {/* Word count delta */}
      {result.original_word_count && (
        <p className="text-[10.5px] text-muted-foreground">
          Word count: {result.original_word_count} → {result.rewrite_word_count}
          {' '}
          ({result.rewrite_word_count > result.original_word_count ? '+' : ''}
          {result.rewrite_word_count - result.original_word_count} words)
        </p>
      )}
      {/* Preview */}
      <ScrollArea className="h-56 rounded border border-panel-border bg-[var(--manuscript-bg,#fdfaf5)] p-3">
        <p className="whitespace-pre-wrap font-serif text-[12.5px] leading-relaxed text-[#1a1a1a]">
          {rewrite.slice(0, 1200)}{rewrite.length > 1200 ? '\n\n…' : ''}
        </p>
      </ScrollArea>
      <Button
        size="sm"
        className="w-full"
        onClick={handleAccept}
        disabled={accepting}
        data-testid="coach-accept-rewrite-btn"
      >
        {accepting
          ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
        {action?.ctaLabel || 'Accept rewrite'}
      </Button>
    </div>
  );
}

// ── Archetype suggestion result ───────────────────────────────────────────────

function ArchetypeSuggestionResult({ result }) {
  const suggestions = result.suggestions || [];
  return (
    <div className="space-y-3" data-testid="coach-result-archetype">
      <div className="flex items-start gap-2 rounded-lg border border-purple-200 bg-purple-50 p-2.5">
        <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-700" />
        <p className="text-[11.5px] text-purple-900 font-medium">
          {suggestions.length} archetype {suggestions.length === 1 ? 'suggestion' : 'suggestions'} from the catalog
        </p>
      </div>
      {suggestions.map((s, i) => (
        <div key={i} className="rounded-lg border border-purple-200 bg-white overflow-hidden"
             data-testid={`archetype-suggestion-${i}`}>
          {/* Character + archetype header */}
          <div className="flex items-center justify-between gap-2 border-b border-purple-100 bg-purple-50/60 px-3 py-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <User className="h-3 w-3 shrink-0 text-purple-600" />
              <span className="text-[11px] font-semibold text-purple-900 truncate">{s.character_name}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Tag className="h-3 w-3 text-purple-500" />
              <span className="text-[11px] font-bold text-purple-800">{s.archetype_name}</span>
            </div>
          </div>
          {/* Blurb + reasoning */}
          <div className="px-3 py-2.5 space-y-1.5">
            {s.blurb && (
              <p className="text-[11px] italic text-muted-foreground">{s.blurb}</p>
            )}
            <div className="flex items-start gap-1.5">
              <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-purple-500" />
              <p className="text-[11.5px] text-foreground leading-snug">{s.reasoning}</p>
            </div>
          </div>
          {/* CTA — open Character Bible */}
          <div className="border-t border-purple-100 bg-purple-50/30 px-3 py-2 flex justify-end">
            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent('loom:open-character-bible', {
                    detail: { characterName: s.character_name },
                  }),
                );
                toast.info(`Opening Character Bible — ${s.character_name} pre-selected.`, { duration: 3000 });
              }}
              className="flex items-center gap-1 text-[10.5px] text-purple-700 hover:text-purple-900 font-medium transition-colors"
              data-testid={`archetype-goto-bible-${i}`}
            >
              <ExternalLink className="h-3 w-3" />
              Apply in Character Bible
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export default function CoachHintDialog({
  open,
  onClose,
  chapterId,
  chapterNumber,
  chapterTitle,
  hintLabel,
  hintBody,
  setChapters,
}) {
  const [activeAction, setActiveAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [beatNote, setBeatNote] = useState('');            // steer for add_scene_beat
  const [characterName, setCharacterName] = useState(''); // optional filter for suggest_archetype

  const runAction = useCallback(async (actionId) => {
    if (loading || !chapterId) return;
    setActiveAction(actionId);
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      const payload = {
        action:       actionId,
        hint_context: `${hintLabel || ''}: ${hintBody || ''}`.trim().slice(0, 300),
      };
      // Include the author-supplied beat direction for add_scene_beat
      if (actionId === 'add_scene_beat' && beatNote.trim()) {
        payload.beat_note = beatNote.trim().slice(0, 300);
      }
      // Include compact archetype catalog + optional character filter for suggest_archetype
      if (actionId === 'suggest_archetype') {
        payload.archetype_catalog = ARCHETYPE_CATALOG;
        if (characterName.trim()) {
          payload.character_name = characterName.trim().slice(0, 80);
        }
      }
      const r = await axios.post(
        `${API}/projects/chapters/${chapterId}/coach-suggest`,
        payload,
        { withCredentials: true, timeout: 120000 },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Claude could not complete this action. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [chapterId, loading, hintLabel, hintBody, beatNote, characterName]);

  const resetAndClose = () => {
    setActiveAction(null);
    setResult(null);
    setError(null);
    setBeatNote('');
    setCharacterName('');
    onClose?.();
  };

  const chapterLabel = chapterTitle || `Chapter ${chapterNumber || '?'}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && resetAndClose()}>
      <DialogContent
        className="flex max-h-[90vh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0"
        data-testid="coach-hint-dialog"
      >
        {/* Header */}
        <DialogHeader className="border-b border-panel-border bg-panel-header px-4 py-3">
          <div className="flex items-center gap-2">
            <Compass className="h-4 w-4 shrink-0 text-[#5a1c1f]" />
            <DialogTitle className="text-[14px] font-semibold text-[#5a1c1f]">
              Coach Actions
            </DialogTitle>
            <span className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {chapterLabel}
            </span>
          </div>
          <DialogDescription className="mt-1 flex items-start gap-1.5 text-[11.5px] text-foreground/80">
            <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
            {hintLabel && <strong>{hintLabel} — </strong>}
            {hintBody || 'Choose an AI action below to edit this chapter with Claude.'}
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Action list */}
          <div className="space-y-2 mb-4" data-testid="coach-action-list">
            {ACTIONS.map((action) => (
              <React.Fragment key={action.id}>
                <ActionButton
                  action={action}
                  activeId={activeAction}
                  loading={loading}
                  onSelect={runAction}
                />
                {/* Beat note textarea — shown directly below add_scene_beat */}
                {action.id === 'add_scene_beat' && (
                  <div className="pl-7 pr-1" data-testid="beat-note-container">
                    <label className="block mb-0.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      Steer the beat <span className="normal-case font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={beatNote}
                      onChange={e => setBeatNote(e.target.value)}
                      disabled={loading}
                      maxLength={300}
                      rows={2}
                      placeholder="e.g. add a moment where Seraphine doubts Dragos"
                      className="w-full resize-none rounded border border-panel-border bg-background px-2 py-1.5 text-[11.5px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-teal-400 disabled:opacity-40"
                      data-testid="beat-note-input"
                    />
                  </div>
                )}
                {/* Character name filter — shown directly below suggest_archetype */}
                {action.id === 'suggest_archetype' && (
                  <div className="pl-7 pr-1" data-testid="archetype-character-filter-container">
                    <label className="block mb-0.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      Focus on character <span className="normal-case font-normal">(optional — leave blank for auto-detect)</span>
                    </label>
                    <input
                      type="text"
                      value={characterName}
                      onChange={e => setCharacterName(e.target.value)}
                      disabled={loading}
                      maxLength={80}
                      placeholder="e.g. Elena, or leave blank to detect all main characters"
                      className="w-full rounded border border-panel-border bg-background px-2 py-1.5 text-[11.5px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-purple-400 disabled:opacity-40"
                      data-testid="archetype-character-name-input"
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center gap-2 rounded-lg border border-panel-border bg-panel-header/50 px-3 py-3 text-[11.5px] text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Claude is reading your chapter…
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <div className="flex-1">
                <p className="text-[12px] font-semibold text-rose-800">Action failed</p>
                <p className="mt-0.5 text-[11.5px] text-rose-700">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => activeAction && runAction(activeAction)}
                className="rounded p-0.5 text-rose-600 hover:bg-rose-100"
                title="Retry"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Result */}
          {result && !loading && (
            result.action === 'insertion_point'
              ? <InsertionPointResult
                  result={result}
                  chapterId={chapterId}
                  onAccepted={resetAndClose}
                />
              : result.action === 'add_scene_beat'
                ? <AddSceneBeatResult
                    result={result}
                    chapterId={chapterId}
                    onAccepted={resetAndClose}
                  />
                : result.action === 'suggest_archetype'
                  ? <ArchetypeSuggestionResult result={result} />
                  : <RewriteResult
                      result={result}
                      chapterId={chapterId}
                      onAccepted={resetAndClose}
                      setChapters={setChapters}
                    />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-panel-border bg-panel-header/60 px-4 py-2 flex justify-end">
          <Button variant="ghost" size="sm" onClick={resetAndClose} data-testid="coach-dialog-close-btn">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
